import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";

import { skillTabForUser, type SkillSummary } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import { Goto } from "@/shared/navigation";

import { SkillInspector } from "../../Skills/SkillInspector";
import { type SkillSelection } from "../buildRequestTemplate";

import styles from "./SkillRow.module.css";

/**
 * Deep link to the Skills page, pre-filtered to this skill and with its
 * versions panel expanded so the user lands on the body editor. The tab must
 * match where the page lists the skill for this user — Personal for one they
 * authored or subscribed to, Org otherwise (see ``skillTabForUser``) — or the
 * name-filtered landing list comes up empty.
 */
function editSkillHref(summary: SkillSummary, userId: string): string {
  const params = new URLSearchParams({
    skillId: summary.skill.skill_id,
    name: summary.skill.name,
    tab: skillTabForUser(summary, userId),
  });
  return `${Goto.route.AgentSkills}?${params.toString()}`;
}

interface SkillRowProps {
  selection: SkillSelection;
  /** The matching org-shared skill (``org`` or ``org-editable``), or
   *  ``undefined`` when the attached ``skill_id`` no longer resolves — the
   *  skill was deleted, or downgraded to private, since it was attached. */
  summary: SkillSummary | undefined;
  /** The signed-in user, used to aim the inspector's "Edit skill" link at the
   *  right tab (see ``editSkillHref``): authorship, not just subscription,
   *  decides whether a skill lists under Personal or Org. */
  userId: string;
  /** When false the row is read-only: the remove button is hidden (for
   *  parity with ``GoalCard``) and the surrounding ``<fieldset>`` disables
   *  the version selector. */
  isEditing: boolean;
  onChange: (next: SkillSelection) => void;
  onRemove: () => void;
}

/**
 * One attached skill: its name, the latest-version description for context,
 * and a version selector that pins an exact version or tracks "Latest".
 *
 * When ``summary`` is undefined the skill no longer resolves; the row
 * renders a remove-me warning instead of a selector so a stale agent can
 * be repaired without guessing which entry is broken.
 */
export function SkillRow({
  selection,
  summary,
  userId,
  isEditing,
  onChange,
  onRemove,
}: SkillRowProps) {
  // No version fetch for an unresolved skill — there is nothing to pin, and
  // its versions endpoint would 404.
  const versionsQuery = useSkillVersions(
    summary !== undefined ? selection.skill_id : undefined,
  );
  const [inspectOpen, setInspectOpen] = React.useState(false);

  if (summary === undefined) {
    return (
      <section className={`${styles.row} ${styles.unavailable}`}>
        {/* The icon tile uses the row's unavailable color via CSS so a
            broken skill reads as red end-to-end (left accent + tile). */}
        <div className={`${styles.iconTile} ${styles.iconTileUnavailable}`}>
          <AutoAwesomeIcon fontSize="small" />
        </div>
        <div className={styles.main}>
          <span className={styles.name}>{selection.skill_id}</span>
          <span className={styles.warning}>
            This skill is no longer available — remove it.
          </span>
        </div>
        {isEditing && (
          <button
            type="button"
            aria-label={`Remove unavailable skill ${selection.skill_id}`}
            className={styles.removeButton}
            onClick={onRemove}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </section>
    );
  }

  const latestVersion = summary.latest_version?.version ?? null;
  const fetchedVersions = (versionsQuery.data ?? []).map((v) => v.version);
  // The pinned version must stay selectable even before the versions fetch
  // settles (or if it was since deleted), so the <select> can always render
  // its current value rather than silently falling back to "Latest".
  const versionNumbers = Array.from(
    new Set(
      selection.version === null
        ? fetchedVersions
        : [...fetchedVersions, selection.version],
    ),
  ).sort((a, b) => b - a);

  const selectId = `agent-skill-version-${selection.skill_id}`;

  return (
    <section className={styles.row}>
      <div className={styles.iconTile}>
        <AutoAwesomeIcon fontSize="small" />
      </div>
      {/* The name / description block opens a read-only overlay with the
          skill's when-to-use and full body, plus a link to edit it on the
          Skills page. It's a role="button" element, not a <button>: the
          surrounding <fieldset disabled> in read mode would disable a real
          form control (so its click would never fire), but inspecting a skill
          must work whether or not the agent is being edited. */}
      <div
        role="button"
        tabIndex={0}
        className={`${styles.main} ${styles.mainButton}`}
        onClick={() => setInspectOpen(true)}
        onKeyDown={(e) => {
          // Mirror a native <button>: Enter activates on keydown; Space is
          // prevented here (so the page doesn't scroll) and activates on keyup
          // below, rather than repeatedly while the key is held.
          if (e.key === "Enter") {
            e.preventDefault();
            setInspectOpen(true);
          } else if (e.key === " ") {
            e.preventDefault();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === " ") {
            e.preventDefault();
            setInspectOpen(true);
          }
        }}
        aria-label={`View skill ${summary.skill.name}`}
      >
        <span className={styles.name}>{summary.skill.name}</span>
        {summary.latest_version !== null && (
          <span className={styles.description}>
            {summary.latest_version.description}
          </span>
        )}
      </div>
      <SkillInspector
        open={inspectOpen}
        onClose={() => setInspectOpen(false)}
        summary={summary}
        editHref={editSkillHref(summary, userId)}
      />
      <div className={styles.versionControl}>
        <label className={styles.versionLabel} htmlFor={selectId}>
          Version
        </label>
        <select
          id={selectId}
          className={styles.versionSelect}
          value={selection.version === null ? "" : String(selection.version)}
          onChange={(e) =>
            onChange({
              ...selection,
              version: e.target.value === "" ? null : Number(e.target.value),
            })
          }
        >
          <option value="">
            {latestVersion === null ? "Latest" : `Latest (v${latestVersion})`}
          </option>
          {versionNumbers.map((version) => (
            <option key={version} value={String(version)}>
              v{version}
            </option>
          ))}
        </select>
      </div>
      {isEditing && (
        <button
          type="button"
          aria-label={`Remove skill ${summary.skill.name}`}
          className={styles.removeButton}
          onClick={onRemove}
        >
          <CloseIcon fontSize="small" />
        </button>
      )}
    </section>
  );
}
